import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import api from '../api/axios';

export const QRScannerModal = ({ isOpen, onClose }) => {
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    let scanner = null;
    if (isOpen && cameraActive && !scanResult && !error) {
      const timer = setTimeout(() => {
        scanner = new Html5QrcodeScanner('qr-modal-reader', {
          qrbox: { width: 220, height: 220 },
          fps: 10,
          supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA]
        });

        scanner.render(onScanSuccess, onScanError);
      }, 200);

      return () => clearTimeout(timer);
    }

    function onScanSuccess(resultText) {
      if (scanner) scanner.clear();
      setCameraActive(false);
      processStationScan(resultText);
    }

    function onScanError(err) {
      // frame scanning loop
    }

    return () => {
      if (scanner) {
        scanner.clear().catch(e => console.error('Error al detener cámara:', e));
      }
    };
  }, [isOpen, cameraActive, scanResult, error]);

  // Reset state when modal is opened or closed
  useEffect(() => {
    if (!isOpen) {
      setScanResult(null);
      setError(null);
      setCameraActive(false);
      setIsProcessing(false);
    }
  }, [isOpen]);

  const processStationScan = async (qrData) => {
    setIsProcessing(true);
    setError(null);
    setScanResult(null);

    try {
      const response = await api.post('/points/register', { qrData });
      setScanResult(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Ocurrió un inconveniente al procesar el código QR. Por favor verifique el código e intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setScanResult(null);
    setError(null);
    setCameraActive(true);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] overflow-y-auto bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0b382c] rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-fadeIn border-2 border-[#00e676]/50 text-white">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#00e676]/30 bg-[#07241c] text-white">
          <div className="flex items-center gap-2">
            <span className="text-xl">📱</span>
            <h3 className="text-sm sm:text-base font-black uppercase tracking-wider text-[#00e676]">Escáner de Puntos QR</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors text-lg font-black"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4 text-center">
          
          {/* Step 1: Manual "Activar Cámara" Trigger Button */}
          {!cameraActive && !scanResult && !error && !isProcessing && (
            <div className="py-6 space-y-6">
              <div className="w-20 h-20 bg-[#00e676]/20 text-[#00e676] rounded-3xl flex items-center justify-center text-4xl mx-auto shadow-md border border-[#00e676]/40">
                📷
              </div>
              <div className="space-y-2 max-w-xs mx-auto">
                <h4 className="font-extrabold text-white text-base uppercase tracking-wider">Escaneo de Código QR</h4>
                <p className="text-xs text-slate-200 font-medium leading-relaxed">
                  Para registrar los puntos de la estación, por favor pulse el botón a continuación para autorizar y activar su cámara.
                </p>
              </div>
              <button
                onClick={() => setCameraActive(true)}
                className="w-full py-4 bg-[#00e676] hover:bg-[#00c853] text-[#07241c] font-black text-sm uppercase tracking-widest rounded-2xl shadow-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <span>📷</span> Activar Cámara
              </button>
            </div>
          )}

          {/* Step 2: Camera Active & Scanner View */}
          {cameraActive && !isProcessing && !scanResult && !error && (
            <div className="space-y-4">
              <div className="bg-amber-400/20 border border-amber-400/40 rounded-2xl p-3 text-amber-300 text-xs font-bold text-center">
                📷 Apunte la cámara hacia el código QR de la estación
              </div>

              {/* QR Reader Container */}
              <div className="w-full">
                <div id="qr-modal-reader" className="w-full text-white" />
              </div>
            </div>
          )}

          {/* Step 3: Processing */}
          {isProcessing && (
            <div className="py-10 space-y-4">
              <div className="w-12 h-12 border-4 border-[#00e676] border-t-transparent rounded-full animate-spin mx-auto" />
              <div>
                <h4 className="font-bold text-white text-sm uppercase tracking-wider">Verificando Código...</h4>
                <p className="text-xs text-slate-300 mt-1">Registrando sus puntos en su cuenta de usuario.</p>
              </div>
            </div>
          )}

          {/* Step 4: Success View */}
          {scanResult && (
            <div className="py-4 space-y-4">
              <div className="w-16 h-16 bg-[#00e676]/20 text-[#00e676] border border-[#00e676]/50 rounded-full flex items-center justify-center text-3xl mx-auto shadow-md">
                ✓
              </div>
              <div className="space-y-2">
                <h4 className="font-extrabold text-white text-base sm:text-lg leading-relaxed">{scanResult.message}</h4>
                {scanResult.stationName && (
                  <p className="text-xs text-slate-300">
                    Estación: <strong className="text-[#00e676] font-bold">{scanResult.stationName}</strong>
                  </p>
                )}
                <div className="mt-2 inline-block bg-[#00e676]/20 text-[#00e676] border border-[#00e676]/40 px-5 py-2 rounded-full font-black text-sm uppercase tracking-wider">
                  Acumulado Total: {scanResult.totalPoints} pts
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleReset}
                  className="flex-1 py-3.5 bg-[#07241c] border border-[#00e676]/40 text-[#00e676] font-black text-xs uppercase tracking-wider rounded-2xl hover:bg-[#07241c]/80 transition-colors shadow-sm"
                >
                  Escanear Otro QR
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3.5 bg-[#00e676] hover:bg-[#00c853] text-[#07241c] font-black text-xs uppercase tracking-wider rounded-2xl transition-colors shadow-md"
                >
                  Finalizar
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Error View (Usted Tone) */}
          {error && (
            <div className="py-4 space-y-4">
              <div className="w-16 h-16 bg-red-500/20 text-red-400 border border-red-500/40 rounded-full flex items-center justify-center text-3xl mx-auto shadow-md">
                ⚠️
              </div>
              <div className="space-y-2">
                <h4 className="font-extrabold text-red-400 text-lg uppercase tracking-wider">Atención</h4>
                <p className="text-xs text-slate-200 font-medium px-2 leading-relaxed">{error}</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleReset}
                  className="flex-1 py-3.5 bg-red-600 hover:bg-red-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-colors shadow-md"
                >
                  Reintentar Escaneo
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3.5 bg-[#07241c] border border-slate-600 text-slate-200 font-bold text-xs uppercase tracking-wider rounded-2xl hover:bg-slate-800 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
